/**
 * IdleSessionGate — the 30-minute sign-out, wired into Designer.
 *
 * Same three jobs as Console's: warn at 28 minutes, replace the app at 30, and
 * in one tab only, record and revoke the session.
 *
 * Designer is the app that matters most here. Console and Explorer mostly show
 * data that is already saved. Designer holds survey structure that exists
 * nowhere else until somebody presses Save. So this gate also carries the
 * plumbing App needs to rescue that work: the idle session is put on a context
 * so App can register a draft saver with it.
 *
 * Nothing here closes a tab. Console opened this one with `window.open`, but it
 * threw away the reference and the reference would not survive a Console reload
 * anyway. So Designer locks itself instead, by watching a cookie the whole
 * family of tabs shares.
 */

import React, { useCallback } from 'react';
import { useIdleSession, IdleLockScreen, IdleWarningBanner } from '@cloudsam/ui';
import { useAuth } from '../context/AuthContext';
import { IdleContext } from '../context/IdleContext';
import { idleClock, IDLE_LOCK_MINUTES } from '../idle-session';
import hanko from '../hanko-init';
import SamLogo from './SamLogo';

export default function IdleSessionGate({ children }) {
    const { isAuthenticated } = useAuth();

    const handleLock = useCallback(async ({ owned }) => {
        // Only the tab that claimed the sign-out talks to the server.
        if (!owned) return;
        // Any draft save has already finished — useIdleSession waits for it —
        // because revoking the token mid-save makes that save fail with a 401,
        // which is the exact thing this feature exists to prevent.
        try {
            await hanko.user.logout();
        } catch (err) {
            // The screen is locked either way, and the server-side idle check
            // is the backstop for a revoke that did not land.
            console.warn('[IdleSessionGate] session revoke failed');
        }
    }, []);

    const idle = useIdleSession({
        clock: idleClock,
        enabled: Boolean(isAuthenticated),
        onLock: handleLock,
    });

    if (idle.isLocked) {
        return (
            <IdleLockScreen
                logo={<SamLogo size={56} showWordmark />}
                appName="Designer"
                reason={idle.reason}
                saving={idle.saving}
                saveResults={idle.saveResults}
                contextLabel={idle.contextLabel}
                idleMinutes={IDLE_LOCK_MINUTES}
            />
        );
    }

    return (
        <IdleContext.Provider value={idle}>
            {children}
            {idle.isWarning && (
                <IdleWarningBanner
                    secondsUntilLock={idle.secondsUntilLock}
                    saving={idle.saving}
                    onStaySignedIn={idle.staySignedIn}
                />
            )}
        </IdleContext.Provider>
    );
}
